/**
 * Analise (spec 062), em tres abas com o mesmo periodo. **Checkout** (fase 1): o funil, por que as pessoas nao compram e
 * quem chamar, contados pelo `pagamentos` (dono do checkout). **Area** (fase 2): logins, tempo na area, telas, pedidos,
 * Academy e quem esta esfriando, contados pelo backend. **Clientes** (fase 3): recompra, da compra ao uso, uso contra
 * resultado, mensalidade por safra e canais, do backend e do `pagamentos`. A tela so mostra. Periodo, aba e checkout moram na URL.
 *
 * O funil conta pessoas (o codigo do navegador) a partir da visita aberta no periodo, e so existe desde que a pagina
 * passou a contar (`contando_desde`). As pistas de pagamento (meio, recusas, PIX vencido, quem nao pagou) vem do banco
 * desde o comeco do sistema novo.
 */
import { periodoAnterior as periodoAnteriorDe } from "@mtc/contratos";
import type { AnaliseDaArea, AnaliseDeClientes, AnaliseDeClientesDoPagamento, AnaliseDoCheckout, GrupoDoResultado, LinhaDaMensalidade, LinhaDoCanal, LinhaDoRecorte, PessoaEsfriando, PessoaQueNaoPagou } from "@mtc/contratos";
import { Abas, Aviso, CampoData, CampoSelecao, Carregando, Comparacao, GraficoDeBarras, GraficoDeLinha, Metrica, Metricas, Pilha, Secao, Segmentado, Tabela, formatarDataHora, formatarDia, formatarReais, hojeEmSaoPaulo } from "@mtc/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { erroDe, useApi } from "../api";
import { config } from "../config";
import { ATALHOS, ehAtalho, erroDoPeriodo, periodoDoAtalho, rotuloDaChave, rotuloDoPeriodo, type Atalho } from "../periodos";

const PAG = (caminho: string) => `${config().PAGAMENTOS_URL.replace(/\/$/, "")}${caminho}`;
const inteiro = (v: number) => Math.round(v).toLocaleString("pt-BR");
const porcento = (t: number | null) => (t == null ? "Sem dado" : `${Math.round(t * 100)}%`);
const taxa = (a: number, b: number) => (b ? a / b : null);
const nota = { margin: 0, fontSize: "var(--t-xs)" } as const;

function segundos(s: number): string {
  return s < 60 ? `${Math.round(s)} s` : `${Math.round(s / 60)} min`;
}

function duracao(minutos: number | null): string {
  if (minutos == null) return "Sem dado";
  if (minutos === 0) return "0 min";
  if (minutos < 1) return "Menos de 1 min";
  if (minutos < 60) return `${Math.round(minutos)} min`;
  if (minutos < 48 * 60) return `${(minutos / 60).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} h`;
  return `${Math.round(minutos / 1440)} dias`;
}

export function PaginaAnalise() {
  const { ler } = useApi();
  const [params, setParams] = useSearchParams();
  const hoje = hojeEmSaoPaulo();
  const atalho: Atalho = ehAtalho(params.get("periodo")) ? (params.get("periodo") as Atalho) : "mes-atual";
  const escolhido = { de: params.get("de") ?? `${hoje.slice(0, 8)}01`, ate: params.get("ate") ?? hoje };
  const periodo = atalho === "datas" ? escolhido : periodoDoAtalho(atalho, hoje);
  const checkout = params.get("checkout") ?? "";
  const aba: "checkout" | "area" | "clientes" = params.get("aba") === "area" ? "area" : params.get("aba") === "clientes" ? "clientes" : "checkout";
  const erro = erroDoPeriodo(periodo);
  const mudar = (o: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(o)) if (v == null || v === "") p.delete(k); else p.set(k, v);
    setParams(p, { replace: true });
  };
  const escolherAtalho = (a: Atalho) => mudar(a === "datas" ? { periodo: a, de: periodo.de, ate: periodo.ate } : { periodo: a === "mes-atual" ? null : a, de: null, ate: null });

  const q = new URLSearchParams({ ...periodo, ...(checkout ? { checkout } : {}) }).toString();
  const analise = useQuery({ queryKey: ["analise", "checkout", q], queryFn: () => ler<AnaliseDoCheckout>(PAG(`/v1/admin/analise/checkout?${q}`)), enabled: !erro && aba === "checkout", placeholderData: (a) => a });
  const checkouts = useQuery({ queryKey: ["checkouts"], queryFn: () => ler<{ itens: { token: string; nome: string; ativo: boolean }[] }>(PAG("/v1/admin/checkouts")), staleTime: 5 * 60_000, enabled: aba === "checkout" });
  const m = analise.data;

  return (
    <Pilha espaco={5}>
      <header style={{ display: "grid", gap: "var(--e-2)" }}>
        <h1 className="mtc-titulo mtc-cabecalho__titulo">Análise</h1>
        <p className="mtc-texto-2" style={{ margin: 0 }}>{aba === "checkout" ? "Quem abre o checkout, até onde vai, por que não compra e quem chamar." : aba === "area" ? "Quantas vezes o cliente entra, quanto tempo fica, o que usa e quem está sumindo." : "Quem volta a comprar, quanto demora pra usar, o que muda o resultado e de onde vem quem compra."}</p>
      </header>
      <Abas rotulo="O que analisar" valor={aba} aoMudar={(a) => mudar({ aba: a === "checkout" ? null : a, checkout: null })} abas={[{ valor: "checkout", rotulo: "Checkout" }, { valor: "area", rotulo: "Área do cliente" }, { valor: "clientes", rotulo: "Clientes" }]} />
      <Pilha espaco={2}>
        <div className="mtc-linha" style={{ gap: "var(--e-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
          <Segmentado rotulo="Período" nome="periodo-da-analise" valor={atalho} aoMudar={escolherAtalho} opcoes={ATALHOS} quebra />
          {atalho === "datas" && <div style={{ flex: "0 1 180px" }}><CampoData rotulo="De" valor={escolhido.de} aoMudar={(de) => mudar({ de })} /></div>}
          {atalho === "datas" && <div style={{ flex: "0 1 180px" }}><CampoData rotulo="Até" valor={escolhido.ate} aoMudar={(ate) => mudar({ ate })} /></div>}
          {aba === "checkout" && <div style={{ flex: "0 1 280px" }}>
            <CampoSelecao rotulo="Checkout" valor={checkout || "todos"} aoMudar={(c) => mudar({ checkout: c === "todos" ? null : c })} opcoes={[{ valor: "todos", rotulo: "Todos os checkouts" }, ...(checkouts.data?.itens ?? []).map((c) => ({ valor: c.token, rotulo: c.ativo ? c.nome : `${c.nome} (desativado)` }))]} />
          </div>}
        </div>
        {erro ? <Aviso tipo="alerta">{erro}</Aviso> : <p className="mtc-texto-2" style={{ margin: 0 }}>{rotuloDoPeriodo(periodo)}, comparado com {rotuloDoPeriodo(periodoAnteriorDe(periodo))}.</p>}
      </Pilha>

      {aba === "area" && !erro && <AbaDaArea consulta={new URLSearchParams(periodo).toString()} />}
      {aba === "clientes" && !erro && <AbaDeClientes consulta={new URLSearchParams(periodo).toString()} />}
      {aba === "checkout" && <>

      {analise.isError && <Aviso tipo="erro">{erroDe(analise.error)}</Aviso>}
      {!m && analise.isLoading && <Carregando compacto texto="Contando as visitas do período" />}
      {m && (
        <>
          {!m.contando_desde ? (
            <Aviso tipo="info">A página do checkout ainda não contou nenhuma visita neste ambiente. O funil aparece depois da primeira; as pistas de pagamento abaixo já valem.</Aviso>
          ) : m.contando_desde > `${periodo.de}T03:00:00.000Z` ? (
            <Aviso tipo="info">As visitas são contadas desde {formatarDataHora(m.contando_desde)}. Antes disso o funil fica vazio; as pistas de pagamento valem o período todo.</Aviso>
          ) : null}
          <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))", alignItems: "start" }}>
            <Funil m={m} />
            <Recortes m={m} />
            <PistasDaPagina m={m} />
            <PistasDoPagamento m={m} />
          </div>
          <QuemNaoPagou consulta={q} />
        </>
      )}
      </>}
    </Pilha>
  );
}

function Funil({ m }: { m: AnaliseDoCheckout }) {
  const abriu = m.funil[0]!;
  const pagou = m.funil.at(-1)!;
  const s = m.sem_conta;
  return (
    <Secao rotulo="Checkout" titulo="Funil">
      <Metricas rotulo="Funil do checkout">
        <Metrica rotulo="Abriram" valor={inteiro(abriu.pessoas)} apoio="Pessoas que abriram um checkout" comparacao={<Comparacao curta atual={abriu.pessoas} anterior={abriu.anterior} />} />
        <Metrica rotulo="Pagaram" valor={inteiro(pagou.pessoas)} comparacao={<Comparacao curta atual={pagou.pessoas} anterior={pagou.anterior} />} />
        <Metrica rotulo="Conversão" valor={porcento(taxa(pagou.pessoas, abriu.pessoas))} apoio="Pagaram de quem abriu" comparacao={<Comparacao curta modo="pontos" atual={taxa(pagou.pessoas, abriu.pessoas)} anterior={taxa(pagou.anterior, abriu.anterior)} />} />
        <Metrica rotulo="Tempo até pagar" valor={duracao(m.minutos_ate_pagar)} apoio="Mediana, de abrir a pagar" />
      </Metricas>
      <GraficoDeBarras
        rotulo="Pessoas em cada passo do checkout"
        itens={m.funil.map((p, i) => ({ chave: p.chave, rotulo: p.rotulo, valor: p.pessoas, detalhe: i === 0 || !m.funil[i - 1]!.pessoas ? undefined : `${porcento(taxa(p.pessoas, m.funil[i - 1]!.pessoas))} do passo anterior` }))}
        vazio="Ninguém abriu um checkout no período."
      />
      <p className="mtc-texto-2" style={nota}>
        Chegaram sem conta: <strong>{inteiro(s.chegaram)}</strong>. Entraram: {inteiro(s.entraram)}. Criaram a conta: {inteiro(s.criaram_conta)}.
        {m.segundos_de_quem_saiu != null && <> Quem saiu sem clicar em pagar ficou {segundos(m.segundos_de_quem_saiu)} na página (mediana).</>}
      </p>
    </Secao>
  );
}

type AbaDoRecorte = "checkout" | "origem" | "campanha" | "aparelho";

function Recortes({ m }: { m: AnaliseDoCheckout }) {
  const [aba, setAba] = useState<AbaDoRecorte>("checkout");
  const linhas: LinhaDoRecorte[] = m.recortes[aba];
  return (
    <Secao rotulo="Checkout" titulo="Quem converte mais">
      <Abas rotulo="Recorte do funil" valor={aba} aoMudar={setAba} abas={[{ valor: "checkout", rotulo: "Checkout" }, { valor: "origem", rotulo: "Origem" }, { valor: "campanha", rotulo: "Campanha" }, { valor: "aparelho", rotulo: "Aparelho" }]} />
      <Tabela
        linhas={linhas}
        chave={(l) => l.chave}
        vazio={<p className="mtc-texto-2" style={{ margin: 0 }}>Nenhuma visita no período.</p>}
        colunas={[
          { chave: "nome", rotulo: aba === "checkout" ? "Checkout" : aba === "origem" ? "Origem" : aba === "campanha" ? "Campanha" : "Aparelho", celula: (l) => <strong>{l.rotulo}</strong> },
          { chave: "abriram", rotulo: "Abriram", alinhar: "direita", celula: (l) => <span className="mtc-mono">{inteiro(l.abriram)}</span> },
          { chave: "pagaram", rotulo: "Pagaram", alinhar: "direita", celula: (l) => <span className="mtc-mono">{inteiro(l.pagaram)}</span> },
          { chave: "taxa", rotulo: "Conversão", alinhar: "direita", celula: (l) => <span className="mtc-mono">{porcento(l.taxa)}</span> },
        ]}
      />
      <p className="mtc-texto-2" style={nota}>Conta cada visita: a mesma pessoa em dois checkouts aparece nos dois. Origem é a UTM do link (utm_source) ou o site de onde a pessoa veio.</p>
    </Secao>
  );
}

function PistasDaPagina({ m }: { m: AnaliseDoCheckout }) {
  const { erros, cupons_recusados } = m.pistas;
  return (
    <Secao rotulo="Por que não comprou" titulo="O que a pessoa viu">
      <GraficoDeBarras rotulo="Erros que apareceram na página, por pessoas" itens={erros.map((e) => ({ chave: `${e.etapa}:${e.mensagem}`, rotulo: e.mensagem, valor: e.pessoas, detalhe: ETAPA[e.etapa] ?? e.etapa }))} tom="erro" vazio="Nenhum erro na página no período." limite={4} />
      {cupons_recusados.length > 0 && <GraficoDeBarras rotulo="Cupons recusados" itens={cupons_recusados.map((c) => ({ chave: c.motivo, rotulo: c.motivo, valor: c.vezes }))} tom="alerta" limite={3} />}
    </Secao>
  );
}
const ETAPA: Record<string, string> = { entrar: "ao entrar", cadastro: "ao criar a conta", senha: "ao trocar a senha", pagar: "ao pagar", cotacao: "no preço", pagina: "na página" };

function PistasDoPagamento({ m }: { m: AnaliseDoCheckout }) {
  const p = m.pistas;
  const pix = p.por_meio.find((x) => x.meio === "PIX");
  const cartao = p.por_meio.find((x) => x.meio === "CARTAO");
  return (
    <Secao rotulo="Por que não comprou" titulo="No pagamento">
      <Metricas rotulo="Pagamentos do período">
        <Metrica rotulo="PIX pagos" valor={pix ? `${inteiro(pix.pagos)} de ${inteiro(pix.gerados)}` : "Nenhum"} apoio={`${inteiro(p.pix_vencidos)} venceram sem pagar`} />
        <Metrica rotulo="Cartões aprovados" valor={cartao ? `${inteiro(cartao.pagos)} de ${inteiro(cartao.gerados)}` : "Nenhum"} apoio="Tentativas no cartão" />
        <Metrica rotulo="Não pagaram" valor={inteiro(p.nao_pagaram)} apoio="Clicaram em pagar e 10 minutos depois não tinham pago" tom={p.nao_pagaram - p.recuperados > 0 ? "alerta" : "neutro"} />
        <Metrica rotulo="Recuperados" valor={inteiro(p.recuperados)} apoio="Desses, pagaram em até 7 dias" />
      </Metricas>
      <GraficoDeBarras rotulo="Recusas do cartão por motivo" itens={p.recusas.map((r) => ({ chave: r.motivo, rotulo: r.motivo, valor: r.vezes }))} tom="erro" vazio="Nenhuma recusa no período." limite={4} />
      {p.recusas_por_bandeira.length > 0 && <p className="mtc-texto-2" style={nota}>Por bandeira: {p.recusas_por_bandeira.map((b) => `${b.bandeira} ${inteiro(b.vezes)}`).join(", ")}.</p>}
    </Secao>
  );
}

/**
 * A lista pra equipe chamar: uma linha por pessoa, a tentativa mais recente dela no periodo. O pagamentos confere na hora
 * (so depois de 10 minutos sem pagar; quem pagou depois sai), e a lista se atualiza sozinha a cada minuto.
 */
function QuemNaoPagou({ consulta }: { consulta: string }) {
  const { ler } = useApi();
  const lista = useQuery({ queryKey: ["analise", "pessoas", consulta], queryFn: () => ler<{ itens: PessoaQueNaoPagou[] }>(PAG(`/v1/admin/analise/checkout/pessoas?${consulta}`)), placeholderData: (a) => a, refetchInterval: 60_000 });
  const itens = lista.data?.itens ?? [];
  return (
    <Secao rotulo="Pra chamar" titulo="Clicaram em pagar e não pagaram">
      <p className="mtc-texto-2" style={{ margin: 0 }}>Só entra quem passou 10 minutos sem pagar. Quem pagou depois sai da lista sozinho.</p>
      {lista.isError && <Aviso tipo="erro">{erroDe(lista.error)}</Aviso>}
      {lista.isLoading ? <Carregando compacto texto="Buscando as pessoas" /> : (
        <Tabela
          linhas={itens}
          chave={(l) => `${l.email}:${l.em}`}
          vazio={<p className="mtc-texto-2" style={{ margin: 0 }}>Ninguém pra chamar: quem clicou em pagar no período pagou ou ainda está nos 10 minutos.</p>}
          colunas={[
            { chave: "pessoa", rotulo: "Pessoa", celula: (l) => <><strong>{l.nome}</strong><span className="mtc-tabela__apoio">{l.email}</span></> },
            { chave: "telefone", rotulo: "Celular", celula: (l) => <span className="mtc-mono">{l.telefone}</span> },
            { chave: "checkout", rotulo: "Checkout", celula: (l) => <>{l.checkout}<span className="mtc-tabela__apoio">{formatarDataHora(l.em)}</span></> },
            { chave: "houve", rotulo: "O que houve", celula: (l) => l.o_que_houve },
          ]}
        />
      )}
      {itens.length >= 200 && <p className="mtc-texto-2" style={nota}>Mostrando as 200 mais recentes. Escolha um período menor pra ver o resto.</p>}
    </Secao>
  );
}

/** O nome de cada tela da area (o primeiro pedaco do endereco), como o cliente ve no menu. */
const TELAS: Record<string, string> = {
  inicio: "Início", planos: "Situação", etapas: "Etapas", agendamento: "Agendamento", solicitacoes: "Solicitações", "minha-conta": "Minha conta",
  comprar: "Comprar plano", mensalidades: "Mensalidades", financeiro: "Histórico de pagamentos", repasses: "Repasses", academy: "Academy", mercado: "Mercado",
  certificados: "Certificados", perfil: "Perfil", notificacoes: "Notificações", regulamento: "Regulamento", pagar: "Pagamento", comprovantes: "Comprovantes",
};
const nomeDaTela = (t: string) => TELAS[t] ?? t.charAt(0).toUpperCase() + t.slice(1).replace(/-/g, " ");
const decimal = (v: number | null, casas = 1) => (v == null ? "Sem dado" : v.toLocaleString("pt-BR", { maximumFractionDigits: casas }));

/** Aba Area (fase 2): os numeros do backend, a serie de quem usou, as telas, os pedidos e quem esta esfriando. */
function AbaDaArea({ consulta }: { consulta: string }) {
  const { ler } = useApi();
  const analise = useQuery({ queryKey: ["analise", "area", consulta], queryFn: () => ler<AnaliseDaArea>(`/v1/admin/analise/area?${consulta}`), placeholderData: (a) => a });
  const m = analise.data;
  if (analise.isError) return <Aviso tipo="erro">{erroDe(analise.error)}</Aviso>;
  if (!m) return <Carregando compacto texto="Contando o uso da área" />;
  const porQuando = m.agrupamento === "dia" ? "dia" : "mês";
  return (
    <>
      {!m.contando_desde ? (
        <Aviso tipo="info">A área ainda não mandou nenhum sinal de uso neste ambiente. O tempo e as telas aparecem depois do primeiro cliente usar; os logins contam desde 25/10/2026.</Aviso>
      ) : m.contando_desde > m.periodo.de ? (
        <Aviso tipo="info">O uso da área é contado desde {formatarDia(m.contando_desde)}. Antes disso o tempo e as telas ficam vazios.</Aviso>
      ) : null}
      <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))", alignItems: "start" }}>
        <Secao rotulo="Área do cliente" titulo="Quem usa">
          <Metricas rotulo="Clientes ativos">
            <Metrica rotulo="Ativos hoje" valor={inteiro(m.ativos_agora.hoje)} apoio={`${inteiro(m.ativos_agora.semana)} nos últimos 7 dias, ${inteiro(m.ativos_agora.mes)} em 30`} />
            <Metrica rotulo="Ativos no período" valor={inteiro(m.ativos.pessoas)} apoio={m.ativos.dias_por_pessoa != null ? `${decimal(m.ativos.dias_por_pessoa)} dias de uso por pessoa` : "Ninguém usou"} comparacao={<Comparacao curta atual={m.ativos.pessoas} anterior={m.ativos.anterior} />} />
            <Metrica rotulo="Logins" valor={inteiro(m.logins.quantidade)} apoio={m.logins.pessoas ? `${inteiro(m.logins.pessoas)} pessoas, ${decimal(m.logins.por_pessoa_por_semana)} por semana cada` : "Ninguém entrou"} comparacao={<Comparacao curta atual={m.logins.quantidade} anterior={m.logins.anterior} />} />
            <Metrica rotulo="Esfriando" valor={inteiro(m.esfriando)} apoio="Entravam toda semana e sumiram há 14 dias" tom={m.esfriando ? "alerta" : "neutro"} />
          </Metricas>
          <GraficoDeLinha rotulo={`Clientes que usaram a área por ${porQuando}`} rotulos={m.chaves.map(rotuloDaChave)} series={[{ nome: "Clientes", valores: m.serie.map((x) => x.pessoas) }]} formatar={inteiro} altura={160} vazio="Ninguém usou a área no período." />
        </Secao>
        <Secao rotulo="Área do cliente" titulo="Tempo na área">
          <Metricas rotulo="Tempo na área">
            <Metrica rotulo="Por dia de uso" valor={m.tempo.por_dia_de_uso != null ? duracao(m.tempo.por_dia_de_uso) : "Sem dado"} apoio="Média de quem entrou no dia" />
            <Metrica rotulo="Por pessoa, por semana" valor={m.tempo.por_pessoa_por_semana != null ? duracao(m.tempo.por_pessoa_por_semana) : "Sem dado"} />
            <Metrica rotulo="Tempo total" valor={duracao(m.tempo.minutos)} comparacao={<Comparacao curta atual={m.tempo.minutos} anterior={m.tempo.anterior} />} />
          </Metricas>
          <GraficoDeBarras rotulo="Telas mais usadas, em minutos" itens={m.telas.map((t) => ({ chave: t.tela, rotulo: nomeDaTela(t.tela), valor: t.minutos, detalhe: `${inteiro(t.pessoas)} ${t.pessoas === 1 ? "pessoa" : "pessoas"}` }))} formatar={(v) => duracao(v)} vazio="Nenhum uso no período." limite={5} />
          <p className="mtc-texto-2" style={nota}>Só conta a aba aberta e visível, com algum movimento nos últimos 5 minutos. A sessão assistida da equipe não entra.</p>
        </Secao>
        {(m.pedidos || m.academy.aulas_concluidas > 0) && (
          <Secao rotulo="Área do cliente" titulo="O que pedem e estudam">
            <Metricas rotulo="Pedidos e Academy">
              {m.pedidos && <Metrica rotulo="Pedidos" valor={inteiro(m.pedidos.quantidade)} apoio={m.pedidos.pessoas ? `${inteiro(m.pedidos.pessoas)} pessoas, ${decimal(m.pedidos.quantidade / m.pedidos.pessoas)} cada` : "Nenhum pedido"} />}
              <Metrica rotulo="Aulas concluídas" valor={inteiro(m.academy.aulas_concluidas)} apoio={`${inteiro(m.academy.pessoas)} ${m.academy.pessoas === 1 ? "pessoa" : "pessoas"} na Academy`} />
            </Metricas>
            {m.pedidos && <GraficoDeBarras rotulo="Pedidos por tipo" itens={m.pedidos.por_tipo.map((t) => ({ chave: t.tipo, rotulo: t.titulo, valor: t.quantidade }))} tom="info" vazio="Nenhum pedido no período." limite={4} />}
          </Secao>
        )}
      </div>
      <Esfriando />
    </>
  );
}

/** Quem entrava toda semana e sumiu (5.4): a lista pra equipe chamar; a linha abre a ficha. */
function Esfriando() {
  const { ler } = useApi();
  const ir = useNavigate();
  const lista = useQuery({ queryKey: ["analise", "esfriando"], queryFn: () => ler<{ itens: PessoaEsfriando[] }>("/v1/admin/analise/area/esfriando") });
  return (
    <Secao rotulo="Pra chamar" titulo="Esfriando">
      {lista.isError && <Aviso tipo="erro">{erroDe(lista.error)}</Aviso>}
      {lista.isLoading ? <Carregando compacto texto="Buscando quem sumiu" /> : (
        <Tabela
          linhas={lista.data?.itens ?? []}
          chave={(l) => l.pessoa_id}
          aoClicar={(l) => ir(`/traders/${l.pessoa_id}`)}
          vazio={<p className="mtc-texto-2" style={{ margin: 0 }}>Ninguém sumiu: quem entrava toda semana continua entrando.</p>}
          colunas={[
            { chave: "pessoa", rotulo: "Cliente", celula: (l) => <><strong>{l.nome}</strong>{l.email && <span className="mtc-tabela__apoio">{l.email}</span>}</> },
            { chave: "telefone", rotulo: "Celular", celula: (l) => <span className="mtc-mono">{l.telefone ?? "Sem celular"}</span> },
            { chave: "planos", rotulo: "Plano", celula: (l) => l.planos.join(", ") },
            { chave: "ultimo", rotulo: "Último uso", celula: (l) => formatarDia(l.ultimo_uso) },
          ]}
        />
      )}
      <p className="mtc-texto-2" style={nota}>Plano ativo, usou a área em cada uma das 4 semanas antes dos últimos 14 dias e não entrou mais.</p>
    </Secao>
  );
}

const emDias = (v: number | null) => (v == null ? "Sem dado" : Math.round(v) === 0 ? "No mesmo dia" : Math.round(v) === 1 ? "1 dia" : `${inteiro(v)} dias`);
const ROTULO_DO_CANAL: Record<string, string> = { direto: "Direto ou sem origem", "sem-registro": "Antes de gravar a origem" };
const numeroCom = (v: number) => <span className="mtc-mono">{inteiro(v)}</span>;
const parte = (a: number, b: number) => <span className="mtc-mono">{porcento(taxa(a, b))}</span>;

/**
 * Aba Clientes (fase 3): quem compra de novo (o faturamento vem do `pagamentos`, a volta depois de reprovar do backend),
 * da compra ao uso, uso contra resultado, mensalidade por safra e os canais. Bloco de modulo desligado nao aparece.
 */
function AbaDeClientes({ consulta }: { consulta: string }) {
  const { ler } = useApi();
  const doBackend = useQuery({ queryKey: ["analise", "clientes", consulta], queryFn: () => ler<AnaliseDeClientes>(`/v1/admin/analise/clientes?${consulta}`), placeholderData: (a) => a });
  const doPagamento = useQuery({ queryKey: ["analise", "clientes-pagamento", consulta], queryFn: () => ler<AnaliseDeClientesDoPagamento>(PAG(`/v1/admin/analise/clientes?${consulta}`)), placeholderData: (a) => a });
  const m = doBackend.data;
  const v = doPagamento.data;
  const erro = doBackend.error ?? doPagamento.error;
  if (erro) return <Aviso tipo="erro">{erroDe(erro)}</Aviso>;
  if (!m || !v) return <Carregando compacto texto="Contando os clientes" />;
  const f = v.faturamento;
  const r = m.recompra_apos_reprovar;
  const u = m.compra_ao_uso;
  return (
    <>
      <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))", alignItems: "start" }}>
        <Secao rotulo="Clientes" titulo="Quem compra de novo">
          <Metricas rotulo="Recompra">
            <Metrica rotulo="Faturamento" valor={formatarReais(f.valor_centavos)} apoio={`${inteiro(f.compras)} ${f.compras === 1 ? "compra" : "compras"} de ${inteiro(f.compradores)} ${f.compradores === 1 ? "pessoa" : "pessoas"}`} comparacao={<Comparacao curta atual={f.valor_centavos} anterior={f.anterior.valor_centavos} />} />
            <Metrica rotulo="De quem já era cliente" valor={formatarReais(f.valor_de_recompra_centavos)} apoio={f.valor_centavos ? `${porcento(taxa(f.valor_de_recompra_centavos, f.valor_centavos))} do faturamento, ${inteiro(f.recompradores)} ${f.recompradores === 1 ? "pessoa" : "pessoas"}` : "Nenhuma compra no período"} comparacao={<Comparacao curta atual={f.valor_de_recompra_centavos} anterior={f.anterior.valor_de_recompra_centavos} />} />
            <Metrica rotulo="Tempo entre compras" valor={emDias(f.dias_entre_compras)} apoio="Mediana, da compra anterior à recompra" />
          </Metricas>
          <Metricas rotulo="Depois de reprovar">
            <Metrica rotulo="Reprovaram" valor={inteiro(r.reprovados)} apoio="Reprovados ou no limite de perda no período" />
            <Metrica rotulo="Compraram de novo" valor={inteiro(r.compraram_de_novo)} apoio={r.reprovados ? `${porcento(taxa(r.compraram_de_novo, r.reprovados))} de quem reprovou, ${inteiro(r.em_7_dias)} em até 7 dias` : "Ninguém reprovou no período"} />
            <Metrica rotulo="Tempo até voltar" valor={emDias(r.dias_ate_comprar)} apoio="Mediana, da reprovação à compra nova" />
          </Metricas>
          <p className="mtc-texto-2" style={nota}>Já era cliente quem tinha pago um checkout antes, aqui ou na plataforma antiga (pelo CPF). Mensalidade não é compra.</p>
        </Secao>
        <Secao rotulo="Clientes" titulo="Da compra ao uso">
          {u.base_do_uso < u.novos && <Aviso tipo="info">{m.uso_desde ? `O uso da área é contado desde ${formatarDia(m.uso_desde)}: só ${inteiro(u.base_do_uso)} dos ${inteiro(u.novos)} clientes novos compraram depois disso.` : "A área ainda não mandou nenhum sinal de uso: o uso aparece depois do primeiro cliente entrar."}</Aviso>}
          <Metricas rotulo="Da compra ao uso">
            <Metrica rotulo="Clientes novos" valor={inteiro(u.novos)} apoio="A primeira compra foi no período" comparacao={<Comparacao curta atual={u.novos} anterior={u.anterior} />} />
            <Metrica rotulo="Usaram a área" valor={porcento(taxa(u.usaram_a_area, u.base_do_uso))} apoio={u.base_do_uso ? `${inteiro(u.usaram_a_area)} de ${inteiro(u.base_do_uso)}, ${inteiro(u.usaram_em_7_dias)} na primeira semana` : "Sem dado de uso"} />
            <Metrica rotulo="Até o primeiro uso" valor={emDias(u.dias_ate_usar)} apoio="Mediana, da compra ao primeiro dia na área" />
            <Metrica rotulo="Fizeram um pedido" valor={inteiro(u.pediram)} apoio={u.pediram ? `O primeiro em ${emDias(u.dias_ate_pedir).toLowerCase()} (mediana)` : "Nenhum cliente novo pediu"} />
            <Metrica rotulo="Estudaram na Academy" valor={inteiro(u.estudaram)} apoio="Concluíram ao menos uma aula" />
            <Metrica rotulo="Nunca entraram" valor={inteiro(u.nunca_entraram)} apoio="Todos com plano em andamento, não só os novos" tom={u.nunca_entraram ? "alerta" : "neutro"} href="/traders?filtro=nunca-entrou" />
          </Metricas>
        </Secao>
      </div>
      {m.resultado && <UsoEResultado r={m.resultado} />}
      {m.mensalidade && <MensalidadePorSafra s={m.mensalidade} />}
      <Canais v={v} />
    </>
  );
}

function TabelaDoResultado({ grupos, rotulo }: { grupos: GrupoDoResultado[]; rotulo: string }) {
  return (
    <Tabela
      linhas={grupos}
      chave={(g) => g.chave}
      colunas={[
        { chave: "grupo", rotulo, celula: (g) => <strong>{g.rotulo}</strong> },
        { chave: "aprovados", rotulo: "Aprovados", alinhar: "direita", celula: (g) => numeroCom(g.aprovados) },
        { chave: "reprovados", rotulo: "Reprovados", alinhar: "direita", celula: (g) => numeroCom(g.reprovados) },
        { chave: "taxa", rotulo: "Aprovação", alinhar: "direita", celula: (g) => <span className="mtc-mono">{porcento(g.taxa)}</span> },
      ]}
    />
  );
}

function UsoEResultado({ r }: { r: NonNullable<AnaliseDeClientes["resultado"]> }) {
  return (
    <Secao rotulo="Clientes" titulo="Uso e resultado">
      {r.finalizados === 0 ? <p className="mtc-texto-2" style={{ margin: 0 }}>Ninguém terminou a avaliação no período.</p> : (
        <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))", alignItems: "start" }}>
          <TabelaDoResultado grupos={r.academy} rotulo="Academy" />
          {r.frequencia_base ? <TabelaDoResultado grupos={r.frequencia} rotulo="Na área" /> : <p className="mtc-texto-2" style={{ margin: 0 }}>A frequência na área aparece quando houver resultado com 4 semanas de uso contado antes dele.</p>}
        </div>
      )}
      <p className="mtc-texto-2" style={nota}>Quem terminou a avaliação no período ({inteiro(r.finalizados)}). A Academy conta aula concluída entre a compra e o resultado; a área, as semanas com uso nas 4 antes do resultado.</p>
    </Secao>
  );
}

function MensalidadePorSafra({ s }: { s: NonNullable<AnaliseDeClientes["mensalidade"]> }) {
  const [por, setPor] = useState<"safra" | "plano">("safra");
  const linhas = por === "safra" ? s.safras.filter((l) => l.entraram > 0).reverse() : s.por_plano;
  return (
    <Secao rotulo="Clientes" titulo="Mensalidade">
      <Abas rotulo="Mensalidade por" valor={por} aoMudar={setPor} abas={[{ valor: "safra", rotulo: "Mês de entrada" }, { valor: "plano", rotulo: "Plano" }]} />
      <Tabela
        linhas={linhas}
        chave={(l: LinhaDaMensalidade) => l.chave}
        vazio={<p className="mtc-texto-2" style={{ margin: 0 }}>Ninguém entrou na mensalidade nos últimos 12 meses.</p>}
        colunas={[
          { chave: "nome", rotulo: por === "safra" ? "Entrada" : "Plano", celula: (l) => <strong>{por === "safra" ? rotuloDaChave(l.chave) : l.rotulo}</strong> },
          { chave: "entraram", rotulo: "Entraram", alinhar: "direita", celula: (l) => numeroCom(l.entraram) },
          { chave: "seguem", rotulo: "Seguem", alinhar: "direita", celula: (l) => numeroCom(l.seguem) },
          { chave: "sairam", rotulo: "Saíram", alinhar: "direita", celula: (l) => <span className="mtc-mono">{inteiro(l.sairam)}{l.por_falta_de_pagamento ? ` (${inteiro(l.por_falta_de_pagamento)} sem pagar)` : ""}</span> },
          { chave: "em-dia", rotulo: "Pagas em dia", alinhar: "direita", celula: (l) => parte(l.pagas_em_dia, l.vencidas) },
          { chave: "atraso", rotulo: "Com atraso", alinhar: "direita", celula: (l) => parte(l.pagas_com_atraso, l.vencidas) },
          { chave: "aberto", rotulo: "Em aberto", alinhar: "direita", celula: (l) => numeroCom(l.em_aberto) },
        ]}
      />
      <p className="mtc-texto-2" style={nota}>Últimos 12 meses, pelo mês em que a mensalidade começou. Só contam as mensalidades que já venceram.</p>
    </Secao>
  );
}

function Canais({ v }: { v: AnaliseDeClientesDoPagamento }) {
  return (
    <Secao rotulo="Clientes" titulo="Por canal">
      <Tabela
        linhas={v.canais}
        chave={(c) => c.chave}
        cartao="media"
        vazio={<p className="mtc-texto-2" style={{ margin: 0 }}>Nenhuma visita nem compra no período.</p>}
        colunas={[
          { chave: "canal", rotulo: "Canal", celula: (c: LinhaDoCanal) => <strong>{ROTULO_DO_CANAL[c.chave] ?? c.chave}</strong> },
          { chave: "visitas", rotulo: "Visitas", alinhar: "direita", celula: (c) => numeroCom(c.visitas) },
          { chave: "conversao", rotulo: "Conversão", alinhar: "direita", celula: (c) => <span className="mtc-mono">{porcento(c.conversao)}</span> },
          { chave: "compras", rotulo: "Compras", alinhar: "direita", celula: (c) => numeroCom(c.compras) },
          { chave: "valor", rotulo: "Faturamento", alinhar: "direita", celula: (c) => <span className="mtc-mono">{formatarReais(c.valor_centavos)}</span> },
          { chave: "ticket", rotulo: "Ticket", alinhar: "direita", celula: (c) => <span className="mtc-mono">{c.ticket_centavos == null ? "Sem dado" : formatarReais(c.ticket_centavos)}</span> },
          { chave: "recompra", rotulo: "Recompra", alinhar: "direita", celula: (c) => parte(c.recompras, c.compras) },
        ]}
      />
      <p className="mtc-texto-2" style={nota}>Canal é a UTM do link (utm_source) ou o site de onde a pessoa veio. Visitas e conversão contam desde que o checkout passou a contar; compras e faturamento, pela origem gravada na compra.</p>
    </Secao>
  );
}
